(() => {
  if (customElements.get('percent-gauge')) return;

  class PercentGauge extends HTMLElement {
    static get observedAttributes() { return ['value', 'color', 'color2',  'thickness', 'size', 'duration']; }
    constructor() {
      super();
      this._value = 0;
      this._target = 0;
      this._size = 120;
      this._thickness = 8;
      this._duration = 1200;
      this._color = '#3b82f6';
      this._color2 = '#7b93a0';
      this._raf = null;
      this._io = null;

      this.attachShadow({ mode: 'open' });
      this._root = document.createElement('div');
      this.shadowRoot.appendChild(this._root);
      this._renderStatic();
    }

    connectedCallback() {
      this._io = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this._value = 0;
            this._updateVisuals(0);
            this._animateTo(this._getAttrValue());
            //this._io.unobserve(this); //para que se anime solo una vez
          }
        });
      }, { threshold: 0.45 }); 

      this._io.observe(this);
    }


    disconnectedCallback() {
      if (this._io) this._io.disconnect();
      if (this._raf) cancelAnimationFrame(this._raf);
    }

    attributeChangedCallback() {
      this._applyAttrs();
      this._updateVisuals(this._value);
      this._animateTo(this._getAttrValue());
    }

    _getAttrValue() {
      const v = Number(this.getAttribute('value') ?? 0);
      return Math.max(0, Math.min(100, isNaN(v) ? 0 : v));
    }

    _applyAttrs() {
      this._target = this._getAttrValue();
      this._size = Math.max(60, Number(this.getAttribute('size') ?? this._size));
      this._thickness = Math.max(2, Number(this.getAttribute('thickness') ?? this._thickness));
      this._duration = Math.max(0, Number(this.getAttribute('duration') ?? this._duration));
      this._color = this.getAttribute('color') || this._color;
      this._color2 = this.getAttribute('color2') || this._color2;
      this._renderStatic();
    }

    _renderStatic() {
      const r = (this._size / 2) - (this._thickness / 2);
      const circumference = 2 * Math.PI * r;

      this._root.innerHTML = `
        <style>
          :host { display:inline-block; line-height:0; }
          .wrap { position:relative; width:${this._size}px; height:${this._size}px; display:inline-grid; place-items:center; }
          svg { transform:rotate(-90deg); width:${this._size}px; height:${this._size}px; }
          .track { stroke:${this._color2}; }
          .bar { stroke:${this._color}; transition:stroke .2s ease; }
          .label { position:absolute; font-weight:700; font-size:${Math.round(this._size*0.36)}px; user-select:none; color: ${this._color}}
        </style>
        <div class="wrap" role="img" aria-label="Progreso: ${this._value}%">
          <svg viewBox="0 0 ${this._size} ${this._size}">
            <circle class="track" cx="${this._size/2}" cy="${this._size/2}" r="${r}" fill="none" stroke-width="${this._thickness}" stroke-linecap="butt" />
            <circle class="bar" cx="${this._size/2}" cy="${this._size/2}" r="${r}" fill="none" stroke-width="${this._thickness}" stroke-linecap="butt" stroke-dasharray="${circumference}" stroke-dashoffset="${circumference}" />
          </svg>
          <div class="label">0%</div>
        </div>
      `;

      this._circumference = circumference;
      this._bar = this.shadowRoot.querySelector('.bar');
      this._label = this.shadowRoot.querySelector('.label');
      this._wrap = this.shadowRoot.querySelector('.wrap');
      this._updateVisuals(this._value);
    }

    _updateVisuals(value) {
      const v = Math.max(0, Math.min(100, value));
      const offset = this._circumference * (1 - v / 100);
      this._bar.style.strokeDashoffset = String(offset);
      this._label.textContent = `${Math.round(v)}%`;
      this._wrap.setAttribute('aria-label', `Progreso: ${Math.round(v)}%`);
      this._value = v;
    }

    _animateTo(target) {
      if (!this.isConnected) return;
      if (this._raf) cancelAnimationFrame(this._raf);

      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReduced || this._duration === 0) {
        this._updateVisuals(target);
        return;
      }

      const start = performance.now();
      const from = this._value;
      const to = target;
      const delta = to - from;
      const dur = this._duration;

      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

      const tick = (now) => {
        const t = Math.min(1, (now - start) / dur);
        const eased = easeOutCubic(t);
        this._updateVisuals(from + delta * eased);
        if (t < 1) this._raf = requestAnimationFrame(tick);
      };
      this._raf = requestAnimationFrame(tick);
    }
  }

  customElements.define('percent-gauge', PercentGauge);
})();
